import { useSelector } from "react-redux";
import { UserFeatureFlagState } from "../../redux/reducers/FeatureFlag";
import { reducerType } from "../../redux/reducers";
import { getFeatureFlags } from "../api/featureFlag/featureFlag";

/**
 * Check whether a particular treatment for a feature is set for the user.
 * If you are checking a feature flag in a component, use useFeatureFlags() instead:
 * @see useFeatureFlags()
 *
 * @param featureFlags - The current feature flag state
 * @param featureId - The feature id to check
 * @param treatmentId - The treatment id to check
 *
 * @returns boolean indicating whether the provided treatmentId and featureId match the user's featureFlag state
 */
export const checkUserFeatureFlag = (
  featureFlags: UserFeatureFlagState[],
  featureId: string,
  treatmentId: string,
): boolean => {
  for (const featureFlag of featureFlags) {
    if (
      featureFlag.featureId === featureId &&
      featureFlag.treatmentId === treatmentId
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Makes a call to GET /featureFlags/user API to get the list of latest feature flags assigned to the user.
 *
 * Note: Calling checkUserFeatureFlag() by passing the local feature flags state may return stale results, since the
 * local state may become stale for a WebApp session that had been opened before the feature flags were updated in the backend.
 * This is especially necessary for scenarios which rely on toggling the defaultTreatment of a feature in Gamma or Prod.
 * The caller of this function will get the latest UserFeatureFlagState[], which can be used to update the local state using dispatch,
 * and call checkUserFeatureFlag() with the updated state.
 *
 * @return UserFeatureFlagState[] - list of features assigned for the user
 */
export const fetchFeatureFlags = async (): Promise<UserFeatureFlagState[]> => {
  const getFeatureFlagResponse = await getFeatureFlags();
  const userFeatureFlagState: UserFeatureFlagState[] = [];
  for (const userFeature of getFeatureFlagResponse.data.userFeatures) {
    userFeatureFlagState.push({
      featureId: userFeature.featureId,
      treatmentId: userFeature.treatmentId,
    });
  }
  return userFeatureFlagState;
};

/**
 * Hook for feature flag state detection
 *
 * @example
 * ```
 * const response = useFeatureFlags(["foo", "bar"]);
 *
 * // type of response is
 * {
 *   isFooEnabled: boolean;
 *   isBarEnabled: boolean;
 * }
 * ```
 *
 * @param featureFlags The list of feature flags to check if enabled
 * @returns an object with properties in the format of is<FlagName>Enabled
 *   that is set to the boolean value that corresponds to the feature flag
 *   ENABLED state. We use fancy TypeScript 5 features to make this type-safe
 */
export const useFeatureFlags = <
  const T extends readonly string[],
  Response = { [K in T[number] as `is${Capitalize<K>}Enabled`]: boolean },
>(
  flagNames: T,
): Response => {
  const featureFlags = useSelector((state: reducerType) => state.FeatureFlag);
  const ret: Record<string, boolean> = {};

  flagNames.forEach((flagName) => {
    const capitalizedFlagName =
      flagName.charAt(0).toUpperCase() + flagName.slice(1);
    ret[`is${capitalizedFlagName}Enabled`] = checkUserFeatureFlag(
      featureFlags,
      flagName,
      "ENABLED",
    );
  });

  return ret as Response;
};
